/* OREN — liquid image distortion, reduced (09/2026). Vanilla WebGL, no libs.
   Applies only to the homepage hero image (not tiles, not products, not logos).
   Faint ripple on hover, no RGB split, about a quarter of the former strength.
   Falls back to the plain <img> when WebGL / reduced-motion / touch. */
(function () {
  'use strict';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.matchMedia('(hover: none)').matches) return;

  var VERT = [
    'attribute vec2 a_pos;',
    'varying vec2 v_uv;',
    'void main(){',
    '  v_uv = a_pos * 0.5 + 0.5;',
    '  v_uv.y = 1.0 - v_uv.y;',
    '  gl_Position = vec4(a_pos, 0.0, 1.0);',
    '}'
  ].join('\n');

  var FRAG = [
    'precision mediump float;',
    'uniform sampler2D u_tex;',
    'uniform vec2 u_res;',
    'uniform vec2 u_imgres;',
    'uniform vec2 u_mouse;',
    'uniform vec2 u_vel;',
    'uniform float u_int;',
    'uniform float u_time;',
    'varying vec2 v_uv;',
    'vec2 coverUV(vec2 uv){',
    '  float ca = u_res.x / u_res.y;',
    '  float ia = u_imgres.x / u_imgres.y;',
    '  vec2 s = (ca > ia) ? vec2(1.0, ia / ca) : vec2(ca / ia, 1.0);',
    '  return (uv - 0.5) * s + 0.5;',
    '}',
    'void main(){',
    '  vec2 uv = v_uv;',
    '  vec2 asp = vec2(u_res.x / u_res.y, 1.0);',
    '  float d = distance(uv * asp, u_mouse * asp);',
    '  float fall = exp(-d * 8.0);',
    '  vec2 dir = (uv - u_mouse) / max(d, 0.001);',
    '  float ripple = sin(d * 20.0 - u_time * 4.0);',
    '  vec2 off = dir * fall * u_int * 0.010 * ripple;',
    '  off += u_vel * fall * u_int * 0.025;',
    '  vec2 fuv = clamp(coverUV(uv + off), 0.001, 0.999);',
    '  vec4 c = texture2D(u_tex, fuv);',
    '  c.a = 1.0;',
    '  gl_FragColor = c;',
    '}'
  ].join('\n');

  var MAX_INTENSITY = 0.15; /* was 0.6 */

  var instances = [];
  var rafRunning = false;

  function LiquidFX(img) {
    this.img = img;
    this.host = img.parentElement;
    this.mouse = { x: 0.5, y: 0.5 };
    this.target = { x: 0.5, y: 0.5 };
    this.vel = { x: 0, y: 0 };
    this.intensity = 0;
    this.targetIntensity = 0;
    this.time = 0;
    this.ok = false;
    this.init();
  }

  LiquidFX.prototype.init = function () {
    var self = this;
    var src = this.img.currentSrc || this.img.src;
    if (!src) return;
    var tex = new Image();
    tex.crossOrigin = 'anonymous';
    tex.onload = function () { self.setup(tex); };
    tex.onerror = function () {};
    tex.src = src;
  };

  LiquidFX.prototype.setup = function (texImg) {
    var canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;pointer-events:none;';
    var gl = canvas.getContext('webgl', { antialias: false, alpha: false, preserveDrawingBuffer: false });
    if (!gl) return;

    var host = this.host;
    var cs = getComputedStyle(host);
    if (cs.position === 'static') host.style.position = 'relative';

    function compile(type, srcStr) {
      var s = gl.createShader(type);
      gl.shaderSource(s, srcStr);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) return null;
      return s;
    }
    var vs = compile(gl.VERTEX_SHADER, VERT);
    var fs = compile(gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;
    var prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    var texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    try {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, texImg);
    } catch (e) {
      return;
    }

    this.gl = gl;
    this.canvas = canvas;
    this.u = {
      res: gl.getUniformLocation(prog, 'u_res'),
      imgres: gl.getUniformLocation(prog, 'u_imgres'),
      mouse: gl.getUniformLocation(prog, 'u_mouse'),
      vel: gl.getUniformLocation(prog, 'u_vel'),
      int: gl.getUniformLocation(prog, 'u_int'),
      time: gl.getUniformLocation(prog, 'u_time')
    };
    this.imgW = texImg.naturalWidth;
    this.imgH = texImg.naturalHeight;

    host.appendChild(canvas);
    this.img.style.opacity = '0';
    this.resize();
    this.ok = true;

    var self = this;
    this.ro = new ResizeObserver(function () { self.resize(); });
    this.ro.observe(host);

    host.addEventListener('mousemove', function (e) {
      var r = host.getBoundingClientRect();
      var nx = (e.clientX - r.left) / r.width;
      var ny = (e.clientY - r.top) / r.height;
      self.vel.x += (nx - self.target.x) * 2.0;
      self.vel.y += (ny - self.target.y) * 2.0;
      self.target.x = nx;
      self.target.y = ny;
      self.targetIntensity = MAX_INTENSITY;
      startLoop();
    });
    host.addEventListener('mouseleave', function () {
      self.targetIntensity = 0;
    });

    this.draw(0.016);
  };

  LiquidFX.prototype.resize = function () {
    if (!this.gl) return;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var r = this.host.getBoundingClientRect();
    var w = Math.max(1, Math.round(r.width * dpr));
    var h = Math.max(1, Math.round(r.height * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
      this.gl.viewport(0, 0, w, h);
    }
    this.draw(0.016);
  };

  LiquidFX.prototype.draw = function (dt) {
    if (!this.gl) return;
    this.time += dt;
    this.mouse.x += (this.target.x - this.mouse.x) * 0.12;
    this.mouse.y += (this.target.y - this.mouse.y) * 0.12;
    this.intensity += (this.targetIntensity - this.intensity) * 0.07;
    this.vel.x *= 0.90;
    this.vel.y *= 0.90;

    var gl = this.gl;
    gl.uniform2f(this.u.res, this.canvas.width, this.canvas.height);
    gl.uniform2f(this.u.imgres, this.imgW, this.imgH);
    gl.uniform2f(this.u.mouse, this.mouse.x, this.mouse.y);
    gl.uniform2f(this.u.vel, this.vel.x, this.vel.y);
    gl.uniform1f(this.u.int, this.intensity);
    gl.uniform1f(this.u.time, this.time);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  };

  LiquidFX.prototype.active = function () {
    return this.ok && (this.intensity > 0.004 || this.targetIntensity > 0);
  };

  var last = 0;
  function loop(t) {
    var dt = Math.min((t - last) / 1000 || 0.016, 0.05);
    last = t;
    var any = false;
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].active()) {
        instances[i].draw(dt);
        any = true;
      }
    }
    if (any) {
      requestAnimationFrame(loop);
    } else {
      rafRunning = false;
    }
  }
  function startLoop() {
    if (!rafRunning) {
      rafRunning = true;
      last = performance.now();
      requestAnimationFrame(loop);
    }
  }

  function boot() {
    /* One deliberately chosen mood image only: the homepage hero. Tiles, products and logos stay untouched. */
    var imgs = document.querySelectorAll('.gx__hero img');
    for (var i = 0; i < imgs.length; i++) {
      (function (img) {
        if (img.complete && img.naturalWidth) {
          instances.push(new LiquidFX(img));
        } else {
          img.addEventListener('load', function () {
            instances.push(new LiquidFX(img));
          }, { once: true });
        }
      })(imgs[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
